/**
 * ReportsPage — read-only operations report for super_admin and
 * contractor_admin. Sections:
 *   1. Room readiness
 *   2. Cleaning operations
 *   3. Fee requests
 *   4. Inspections by tier
 *
 * Server-side aggregation (see useReportMetrics). No mutations. Page
 * is RouteRole-gated upstream so the role check here is for empty-
 * state rendering only.
 *
 * Spec: PROJECT_STATE task #78.
 */

import { useState, type ReactNode }        from 'react'
import { useTranslation }                  from 'react-i18next'
import {
  useReportMetrics,
  type CountersByCleanerRow,
  type InspectionsTierRow,
  type ReportRoomStatus,
  type ReportFeeRequestStatus,
}                                          from '@/hooks/useReportMetrics'
import {
  useRoomDiscoveryReport,
  useRoomDiscoveryReportAllOrgs,
}                                          from '@/hooks/useRoomDiscoveryReport'
import { useUserOrgMemberships }           from '@/hooks/useUserOrgMemberships'

const ROOM_STATUS_ORDER: readonly ReportRoomStatus[] = [
  'ready',
  'occupied',
  'cleaning_in_progress',
  'cleaning_assigned',
  'inspection_pending',
  'maintenance_required',
  'vacant_dirty',
  'move_out_pending',
  'reserved',
] as const

const FEE_STATUS_ORDER: readonly ReportFeeRequestStatus[] = [
  'requested',
  'approved',
  'denied',
  'completed_without_approval',
  'paid_at_normal_clean_rate',
] as const

export function ReportsPage(): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading, error } = useReportMetrics()
  const memberships = useUserOrgMemberships()
  const isMultiOrg  = (memberships.data?.length ?? 0) > 1
  const [allOrgs, setAllOrgs]       = useState(false)
  const [buildingFilter, setBuildingFilter] = useState<string>('all')
  const discovery        = useRoomDiscoveryReport(7)
  const discoveryAllOrgs = useRoomDiscoveryReportAllOrgs(7, { enabled: isMultiOrg && allOrgs })

  const filteredDiscovery = (() => {
    if (!discovery.data) return null
    if (buildingFilter === 'all') return discovery.data
    const rows = discovery.data.byBuilding.filter((r) => (r.buildingId ?? '__unknown') === buildingFilter)
    return {
      rangeDays:       discovery.data.rangeDays,
      totalEvents:     rows.reduce((s, r) => s + r.totalEvents, 0),
      uniqueRoomCount: rows.reduce((s, r) => s + r.uniqueRoomCount, 0),
      byBuilding:      rows,
    }
  })()

  if (isLoading) {
    return (
      <main data-testid="reports-page" style={pageStyle}>
        <h1 style={titleStyle}>{t('reports.title', 'Reports')}</h1>
        <p style={{ color: 'var(--brand-neutral-500)' }}>{t('reports.loading', 'Loading reports…')}</p>
      </main>
    )
  }

  if (error || !data) {
    return (
      <main data-testid="reports-page" style={pageStyle}>
        <h1 style={titleStyle}>{t('reports.title', 'Reports')}</h1>
        <p role="alert" data-testid="reports-error" style={{ color: 'var(--brand-danger)' }}>
          {t('reports.error', 'Could not load reports. Try refreshing.')}
        </p>
      </main>
    )
  }

  return (
    <main data-testid="reports-page" style={pageStyle}>
      <h1 style={titleStyle}>{t('reports.title', 'Reports')}</h1>

      <Section
        testId="reports-room-readiness"
        heading={t('reports.roomReadiness', 'Room readiness')}
      >
        <Grid>
          {ROOM_STATUS_ORDER.map((s) => (
            <Stat
              key={s}
              testId={`reports-room-${s}`}
              label={labelForRoomStatus(s)}
              value={data.rooms_by_status[s] ?? 0}
            />
          ))}
        </Grid>
      </Section>

      <Section
        testId="reports-cleaning-operations"
        heading={t('reports.cleaningOperations', 'Cleaning operations')}
      >
        <Grid>
          <Stat
            testId="reports-partial-unit-approved"
            label={t('reports.partialUnitApproved', 'Partial-unit completed')}
            value={data.partial_unit_approved}
          />
          <Stat
            testId="reports-exceedance-count"
            label={t('reports.exceedanceCount', 'Exceedance events recorded')}
            value={data.exceedance_count}
          />
          <Stat
            testId="reports-total-trash-bags"
            label={t('reports.totalTrashBags', 'Trash bags collected')}
            value={data.total_trash_bags}
          />
          <Stat
            testId="reports-total-appliances-cleaned"
            label={t('reports.totalAppliancesCleaned', 'Appliances cleaned')}
            value={data.total_appliances_cleaned}
          />
        </Grid>
      </Section>

      <Section
        testId="reports-counters-by-cleaner"
        heading={t('reports.countersByWorker', 'Per-worker totals (payroll)')}
      >
        {data.counters_by_cleaner.length === 0 ? (
          <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
            {t('reports.noCounters', 'No submitted tasks yet — nothing to total.')}
          </p>
        ) : (
          <CleanerTotalsTable rows={data.counters_by_cleaner} />
        )}
      </Section>

      <Section
        testId="reports-fee-requests"
        heading={t('reports.feeRequests', 'Fee requests')}
      >
        <Grid>
          {FEE_STATUS_ORDER.map((s) => (
            <Stat
              key={s}
              testId={`reports-fee-${s}`}
              label={labelForFeeStatus(s)}
              value={data.fee_requests_by_status[s] ?? 0}
              highlight={s === 'paid_at_normal_clean_rate' && (data.paid_at_normal_rate_count > 0)}
            />
          ))}
        </Grid>
      </Section>

      <Section
        testId="reports-room-discovery"
        heading={t('reports.roomDiscovery', 'Room discovery (last 7 days)')}
      >
        {isMultiOrg && (
          <label
            data-testid="reports-discovery-all-orgs-toggle"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}
          >
            <input
              type="checkbox"
              data-testid="reports-discovery-all-orgs-checkbox"
              checked={allOrgs}
              onChange={(e) => setAllOrgs(e.target.checked)}
            />
            {t('reports.allMyOrgs', 'Show data across all my organizations')}
          </label>
        )}

        {allOrgs ? (
          discoveryAllOrgs.isLoading ? (
            <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
              {t('reports.loading', 'Loading reports…')}
            </p>
          ) : discoveryAllOrgs.error || !discoveryAllOrgs.data ? (
            <p role="alert" data-testid="reports-room-discovery-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem' }}>
              {t('reports.roomDiscoveryError', 'Could not load room discovery activity.')}
            </p>
          ) : (
            <>
              <Grid>
                <Stat
                  testId="reports-discovery-total-events"
                  label={t('reports.discoveryTotalEvents', 'Claim events')}
                  value={discoveryAllOrgs.data.totalEvents}
                />
                <Stat
                  testId="reports-discovery-unique-rooms"
                  label={t('reports.discoveryUniqueRooms', 'Distinct rooms touched')}
                  value={discoveryAllOrgs.data.uniqueRoomCount}
                />
              </Grid>
              {discoveryAllOrgs.data.byOrgAndBuilding.length === 0 ? (
                <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
                  {t('reports.discoveryNone', 'No rooms claimed yet in the last 7 days.')}
                </p>
              ) : (
                <DiscoveryMultiOrgTable rows={discoveryAllOrgs.data.byOrgAndBuilding} />
              )}
            </>
          )
        ) : discovery.isLoading ? (
          <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
            {t('reports.loading', 'Loading reports…')}
          </p>
        ) : discovery.error || !discovery.data || !filteredDiscovery ? (
          <p role="alert" data-testid="reports-room-discovery-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem' }}>
            {t('reports.roomDiscoveryError', 'Could not load room discovery activity.')}
          </p>
        ) : (
          <>
            {discovery.data.byBuilding.length > 0 && (
              <label
                data-testid="reports-discovery-building-filter-label"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}
              >
                {t('reports.buildingFilter', 'Building')}
                <select
                  data-testid="reports-discovery-building-filter"
                  value={buildingFilter}
                  onChange={(e) => setBuildingFilter(e.target.value)}
                  style={{ fontSize: '0.85rem', padding: '0.2rem 0.4rem' }}
                >
                  <option value="all">{t('reports.allBuildings', 'All buildings')}</option>
                  {discovery.data.byBuilding.map((r) => (
                    <option key={r.buildingId ?? '__unknown'} value={r.buildingId ?? '__unknown'}>
                      {r.buildingName}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Grid>
              <Stat
                testId="reports-discovery-total-events"
                label={t('reports.discoveryTotalEvents', 'Claim events')}
                value={filteredDiscovery.totalEvents}
              />
              <Stat
                testId="reports-discovery-unique-rooms"
                label={t('reports.discoveryUniqueRooms', 'Distinct rooms touched')}
                value={filteredDiscovery.uniqueRoomCount}
              />
            </Grid>
            {filteredDiscovery.byBuilding.length === 0 ? (
              <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
                {t('reports.discoveryNone', 'No rooms claimed yet in the last 7 days.')}
              </p>
            ) : (
              <DiscoveryTable rows={filteredDiscovery.byBuilding} />
            )}
          </>
        )}
      </Section>

      <Section
        testId="reports-inspections-by-tier"
        heading={t('reports.inspectionsByTier', 'Inspections by tier')}
      >
        {data.inspections_by_tier.length === 0 ? (
          <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
            {t('reports.noInspections', 'No inspections recorded yet.')}
          </p>
        ) : (
          <TierTable rows={data.inspections_by_tier} />
        )}
      </Section>
    </main>
  )
}

function Section(
  { testId, heading, children }: { testId: string; heading: string; children: unknown },
): ReactNode {
  return (
    <section
      data-testid={testId}
      style={{
        background:    'var(--brand-white)',
        border:        '1px solid var(--brand-neutral-300)',
        borderRadius:  'var(--brand-radius-lg)',
        padding:       '1rem',
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.6rem',
      }}
    >
      <h2 style={{ margin: 0, fontSize: '1rem' }}>{heading}</h2>
      {children as ReactNode}
    </section>
  )
}

function Grid({ children }: { children: unknown }): ReactNode {
  return (
    <div style={{
      display:             'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap:                 '0.6rem',
    }}>
      {children as ReactNode}
    </div>
  )
}

function Stat(
  { testId, label, value, highlight }: { testId: string; label: string; value: number; highlight?: boolean },
): ReactNode {
  return (
    <div
      data-testid={testId}
      data-value={value}
      style={{
        padding:      '0.6rem',
        background:   highlight ? 'var(--brand-primary-subtle)' : 'var(--brand-neutral-50, var(--brand-neutral-100))',
        borderRadius: 'var(--brand-radius-sm)',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.2rem',
      }}
    >
      <span style={{ fontSize: '0.75rem', color: 'var(--brand-neutral-700)' }}>{label}</span>
      <strong style={{ fontSize: '1.4rem' }}>{value.toLocaleString()}</strong>
    </div>
  )
}

function DiscoveryMultiOrgTable(
  { rows }: { rows: ReadonlyArray<{ organizationId: string; organizationName: string; buildingId: string | null; buildingName: string; totalEvents: number; uniqueRoomCount: number }> },
): ReactNode {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid="reports-discovery-multi-org-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--brand-neutral-700)' }}>
            <th style={cellStyle}>Organization</th>
            <th style={cellStyle}>Building</th>
            <th style={cellStyle}>Distinct rooms</th>
            <th style={cellStyle}>Claim events</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={`${r.organizationId}-${r.buildingId ?? '__unknown'}`}
              data-testid={`reports-discovery-mo-row-${r.organizationId}-${r.buildingId ?? 'unknown'}`}
            >
              <td style={cellStyle}>{r.organizationName}</td>
              <td style={cellStyle}>{r.buildingName}</td>
              <td style={cellStyle}>{r.uniqueRoomCount}</td>
              <td style={cellStyle}>{r.totalEvents}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DiscoveryTable(
  { rows }: { rows: ReadonlyArray<{ buildingId: string | null; buildingName: string; totalEvents: number; uniqueRoomCount: number }> },
): ReactNode {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid="reports-discovery-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--brand-neutral-700)' }}>
            <th style={cellStyle}>Building</th>
            <th style={cellStyle}>Distinct rooms</th>
            <th style={cellStyle}>Claim events</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.buildingId ?? '__unknown'} data-testid={`reports-discovery-row-${r.buildingId ?? 'unknown'}`}>
              <td style={cellStyle}>{r.buildingName}</td>
              <td style={cellStyle} data-testid={`reports-discovery-${r.buildingId ?? 'unknown'}-rooms`}>{r.uniqueRoomCount}</td>
              <td style={cellStyle} data-testid={`reports-discovery-${r.buildingId ?? 'unknown'}-events`}>{r.totalEvents}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CleanerTotalsTable({ rows }: { rows: CountersByCleanerRow[] }): ReactNode {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid="reports-cleaner-totals-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--brand-neutral-700)' }}>
            <th style={cellStyle}>Worker</th>
            <th style={cellStyle}>Role</th>
            <th style={cellStyle}>Trash bags</th>
            <th style={cellStyle}>Appliances</th>
            <th style={cellStyle}>Tasks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cleaner_id} data-testid={`reports-cleaner-row-${r.cleaner_id}`}>
              <td style={cellStyle}>{r.cleaner_name}</td>
              <td style={cellStyle} data-testid={`reports-cleaner-${r.cleaner_id}-role`}>
                {r.assignee_role ? r.assignee_role.replace(/_/g, ' ') : '—'}
              </td>
              <td style={cellStyle} data-testid={`reports-cleaner-${r.cleaner_id}-bags`}>{r.trash_bag_total}</td>
              <td style={cellStyle} data-testid={`reports-cleaner-${r.cleaner_id}-appliances`}>{r.appliances_total}</td>
              <td style={cellStyle} data-testid={`reports-cleaner-${r.cleaner_id}-tasks`}>{r.tasks_total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TierTable({ rows }: { rows: InspectionsTierRow[] }): ReactNode {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid="reports-tier-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--brand-neutral-700)' }}>
            <th style={cellStyle}>Tier</th>
            <th style={cellStyle}>Passed</th>
            <th style={cellStyle}>Failed</th>
            <th style={cellStyle}>Re-clean</th>
            <th style={cellStyle}>Blocked</th>
            <th style={cellStyle}>Cancelled</th>
            <th style={cellStyle}>Terminal total</th>
            <th style={cellStyle}>Pass rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.service_tier} data-testid={`reports-tier-row-${r.service_tier}`}>
              <td style={cellStyle}><code>{r.service_tier}</code></td>
              <td style={cellStyle} data-testid={`reports-tier-${r.service_tier}-passed`}>{r.passed}</td>
              <td style={cellStyle}>{r.failed}</td>
              <td style={cellStyle}>{r.re_clean_required}</td>
              <td style={cellStyle}>{r.blocked}</td>
              <td style={cellStyle}>{r.cancelled}</td>
              <td style={cellStyle}>{r.terminal_total}</td>
              <td style={cellStyle} data-testid={`reports-tier-${r.service_tier}-pass-rate`}>
                {r.pass_rate === null ? '—' : `${Math.round(r.pass_rate * 1000) / 10}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const pageStyle = {
  display:        'flex',
  flexDirection:  'column' as const,
  gap:            '1rem',
  padding:        '1rem',
}

const titleStyle = {
  margin:     0,
  fontSize:   '1.4rem',
  fontFamily: 'var(--brand-font-family-display)',
}

const cellStyle = {
  padding:      '0.35rem 0.6rem',
  borderBottom: '1px solid var(--brand-neutral-100)',
}

function labelForRoomStatus(s: ReportRoomStatus): string {
  const map: Record<ReportRoomStatus, string> = {
    occupied:               'Occupied',
    move_out_pending:       'Move-out pending',
    vacant_dirty:           'Vacant (dirty)',
    cleaning_assigned:      'Cleaning assigned',
    cleaning_in_progress:   'Cleaning in progress',
    inspection_pending:     'Inspection pending',
    maintenance_required:   'Maintenance required',
    ready:                  'Ready',
    reserved:               'Reserved',
  }
  return map[s]
}

function labelForFeeStatus(s: ReportFeeRequestStatus): string {
  const map: Record<ReportFeeRequestStatus, string> = {
    requested:                  'Requested',
    approved:                   'Approved',
    denied:                     'Denied',
    completed_without_approval: 'Completed without approval',
    paid_at_normal_clean_rate:  'Paid at Normal rate',
  }
  return map[s]
}
